import {FC, useState} from 'react';

import AddressDetails from './AddressDetails';
import BusinessContact from './BusinessContact';
import Preview from './Preview';
import {PartialAddressType, PartialFormData} from './types';

type PageName =
    | 'business-contact'
    | 'partner-address'
    | 'preview'
    | 'signup-address';

export const toggleAddressFormat = (address: PartialAddressType) => {
    const updated = {...address};

    if (updated.country !== 'JP') {
        updated.addressFormat = 'default';

        return updated;
    }

    if (updated.addressFormat === 'jp_default') {
        updated.addressFormat = 'jp_western';

        return updated;
    }

    updated.addressFormat = 'jp_default';

    return updated;
};

const Setup: FC = () => {
    const [page, setPage] = useState<PageName>('business-contact');

    const [formData, setFormData] = useState<PartialFormData>({
        addresses: {
            signupAddress: {
                country: 'JP',
                locality: '',
                region: '',
                streetAddress: '',
            },
        },
    });

    const getPrefillData = (
        type: 'partner-address' | 'signup-address',
    ): PartialAddressType => ({
        addressFormat:
            formData.addresses[
                type === 'signup-address' ? 'signupAddress' : 'partnerAddress'
            ]?.addressFormat || 'jp_default',
        country:
            formData.addresses[
                type === 'signup-address' ? 'signupAddress' : 'partnerAddress'
            ]?.country || 'JP',
        extendedAddress:
            formData.addresses[
                type === 'signup-address' ? 'signupAddress' : 'partnerAddress'
            ]?.extendedAddress,
        faxNumber:
            (type === 'partner-address' && formData.partnerFaxNumber) || '',
        locality:
            formData.addresses[
                type === 'signup-address' ? 'signupAddress' : 'partnerAddress'
            ]?.locality || '',
        phoneNumber:
            (type === 'partner-address' && formData.partnerPhoneNumber) || '',
        postalCode:
            formData.addresses[
                type === 'signup-address' ? 'signupAddress' : 'partnerAddress'
            ]?.postalCode,
        region:
            formData.addresses[
                type === 'signup-address' ? 'signupAddress' : 'partnerAddress'
            ]?.region || '',
        streetAddress:
            formData.addresses[
                type === 'signup-address' ? 'signupAddress' : 'partnerAddress'
            ]?.streetAddress || '',
    });

    if (page === 'business-contact') {
        return (
            <BusinessContact
                onSuccess={(data) => {
                    setFormData({
                        ...formData,
                        signupBusinessEmail: data.signupBusinessEmail,
                        signupFaxNumber: data.signupFaxNumber,
                        signupPhoneNumber: data.signupPhoneNumber,
                    });
                    setPage('signup-address');
                }}
                prefilledData={{
                    signupBusinessEmail: formData.signupBusinessEmail,
                    signupFaxNumber: formData.signupFaxNumber,
                    signupPhoneNumber: formData.signupPhoneNumber,
                }}
            />
        );
    }

    if (page === 'signup-address') {
        return (
            <AddressDetails
                onBack={() => {
                    setPage('business-contact');
                }}
                onSuccess={(data) => {
                    const signupAddress: PartialAddressType = {
                        addressFormat: data.addressFormat,
                        country: data.country,
                        extendedAddress: data.extendedAddress,
                        locality: data.locality,
                        postalCode: data.postalCode,
                        region: data.region,
                        streetAddress: data.streetAddress,
                    };
                    setFormData({
                        ...formData,
                        addresses: {
                            ...formData.addresses,
                            signupAddress,
                        },
                    });
                    setPage('preview');
                }}
                prefillData={getPrefillData('signup-address')}
            />
        );
    }

    if (page === 'partner-address') {
        return (
            <AddressDetails
                onBack={() => {
                    setPage('preview');
                }}
                onSuccess={(data) => {
                    const partnerAddress: PartialAddressType = {
                        addressFormat: data.addressFormat,
                        country: data.country,
                        extendedAddress: data.extendedAddress,
                        locality: data.locality,
                        postalCode: data.postalCode,
                        region: data.region,
                        streetAddress: data.streetAddress,
                    };
                    setFormData({
                        ...formData,
                        addresses: {
                            ...formData.addresses,
                            partnerAddress,
                        },
                        partnerFaxNumber: data.faxNumber,
                        partnerPhoneNumber: data.phoneNumber,
                    });
                    setPage('preview');
                }}
                prefillData={getPrefillData('partner-address')}
                showAlternativeText={true}
                showFax={true}
                showTelephone={true}
            />
        );
    }

    return (
        <Preview
            onAddNewAddress={() => setPage('partner-address')}
            onBack={() => setPage('signup-address')}
            onDelete={() => {
                setFormData((prev) => ({
                    ...prev,
                    addresses: {
                        signupAddress: prev.addresses.signupAddress,
                    },
                }));
            }}
            onEditExistingNewAddress={(target) =>
                setPage(
                    target === 'signupAddress'
                        ? 'signup-address'
                        : 'partner-address',
                )
            }
            onFormatChange={(target) => {
                const targetAddress = formData.addresses[target];

                if (!targetAddress) {
                    return;
                }

                const updatedAddress = toggleAddressFormat(targetAddress);
                setFormData({
                    ...formData,
                    addresses: {
                        ...formData.addresses,
                        [target]: updatedAddress,
                    },
                });
            }}
            prefillData={formData}
        />
    );
};

export default Setup;
